/**
 * DecisionFlow — VerifyEmail
 * ui/src/pages/VerifyEmail.jsx  (or ui/src/components/auth/VerifyEmail.jsx)
 * IAM-P1 / OI-S228-04 / S233
 *
 * Handles the verification link clicked from the provision email.
 * Route: /verify-email?token=xxx
 *
 * States:
 *   loading     → validating token (no UI interaction yet)
 *   set_password → token valid, show set-password form
 *   success     → password set, account active, redirect to login
 *   error       → token invalid / expired / already used
 *
 * POST /api/v1/admin/tenants/verify-email
 *   body: { token, password }
 *   200: { verified: true, tenant_id, user_id, message }
 *   400: TOKEN_EXPIRED | TOKEN_ALREADY_USED | INVALID_TOKEN
 *   422: PASSWORD_TOO_SHORT
 */

import { useState, useEffect } from "react";
import { Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const API_BASE = "/api/v1";
const LOGIN_PATH = "/login";
const MIN_PASSWORD_LENGTH = 12;

// ---------------------------------------------------------------------------
// Password strength indicator
// ---------------------------------------------------------------------------

function passwordStrength(pwd) {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 12) score++;
  if (pwd.length >= 16) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: "Weak",   color: "rgb(var(--color-danger))" };
  if (score <= 3) return { score, label: "Fair",   color: "rgb(var(--color-warning))" };
  return            { score, label: "Strong", color: "rgb(var(--color-success))" };
}

// ---------------------------------------------------------------------------
// Layout shell — no nav, clean centred card
// ---------------------------------------------------------------------------

function AuthCard({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[rgb(var(--color-bg))] px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-8 shadow-sm">
        <div className="mb-6">
          <p className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">DecisionFlow</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">by data-fit LLC</p>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <AuthCard>
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[rgb(var(--color-primary))]" />
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Verifying your link…</p>
      </div>
    </AuthCard>
  );
}

function ErrorState({ code, message }) {
  const friendly = {
    INVALID_TOKEN:      "This verification link is invalid. It may have been copied incorrectly.",
    TOKEN_EXPIRED:      "This verification link has expired (links are valid for 72 hours). Please contact your administrator to resend.",
    TOKEN_ALREADY_USED: "This verification link has already been used. If you need to reset your password, contact your administrator.",
  }[code] ?? message ?? "Something went wrong. Please try again or contact support.";

  return (
    <AuthCard>
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--color-danger))] bg-opacity-10">
          <AlertCircle className="h-6 w-6 text-[rgb(var(--color-danger))]" />
        </div>
        <div>
          <h1 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            Link unavailable
          </h1>
          <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))] leading-relaxed">
            {friendly}
          </p>
        </div>
        <a
          href="mailto:sales@data-fit.com"
          className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
        >
          Contact support
        </a>
      </div>
    </AuthCard>
  );
}

function SuccessState() {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const t = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) {
          clearInterval(t);
          window.location.href = LOGIN_PATH;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <AuthCard>
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--color-success))] bg-opacity-10">
          <CheckCircle2 className="h-6 w-6 text-[rgb(var(--color-success))]" />
        </div>
        <div>
          <h1 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            Account activated
          </h1>
          <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Your password has been set. Redirecting to login in {countdown}…
          </p>
        </div>
        <a
          href={LOGIN_PATH}
          className="text-tok-sm font-semibold text-[rgb(var(--color-primary))] hover:underline"
        >
          Go to login now
        </a>
      </div>
    </AuthCard>
  );
}

function SetPasswordForm({ token, onSuccess, onError }) {
  const [password,    setPassword]    = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [showPwd,     setShowPwd]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [fieldError,  setFieldError]  = useState(null);

  const strength = passwordStrength(password);
  const mismatch = confirm && password !== confirm;
  const tooShort = password && password.length < MIN_PASSWORD_LENGTH;

  async function handleSubmit(e) {
    e.preventDefault();
    setFieldError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFieldError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/verify-email`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = data?.detail?.error?.code;
        const msg  = data?.detail?.error?.message ?? `Error ${res.status}`;
        if (["INVALID_TOKEN", "TOKEN_EXPIRED", "TOKEN_ALREADY_USED"].includes(code)) {
          onError(code, msg);
        } else if (code === "PASSWORD_TOO_SHORT") {
          setFieldError(msg);
        } else {
          setFieldError(msg);
        }
        return;
      }

      onSuccess();
    } catch (e) {
      setFieldError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard>
      <h1 className="mb-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
        Set your password
      </h1>
      <p className="mb-6 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Choose a strong password to activate your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Password */}
        <div className="space-y-1">
          <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Password <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              placeholder="Minimum 12 characters"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
            >
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* Strength bar */}
          {password && (
            <div className="space-y-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-1 flex-1 rounded-full transition-all"
                    style={{
                      background: i <= strength.score ? strength.color : "rgb(var(--color-border))",
                    }}
                  />
                ))}
              </div>
              <p className="text-tok-xs" style={{ color: strength.color }}>{strength.label}</p>
            </div>
          )}

          {tooShort && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">
              At least {MIN_PASSWORD_LENGTH} characters required.
            </p>
          )}
        </div>

        {/* Confirm */}
        <div className="space-y-1">
          <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Confirm password <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
              placeholder="Re-enter your password"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {mismatch && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">Passwords do not match.</p>
          )}
        </div>

        {/* Field error */}
        {fieldError && (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
            {fieldError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || tooShort || mismatch}
          className="w-full rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2.5 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition"
        >
          {submitting ? "Activating…" : "Set password & activate account"}
        </button>
      </form>
    </AuthCard>
  );
}

// ---------------------------------------------------------------------------
// VerifyEmail — root component
// ---------------------------------------------------------------------------

export default function VerifyEmail() {
  const token = new URLSearchParams(window.location.search).get("token");

  const [phase, setPhase]   = useState(token ? "set_password" : "error");
  const [errCode, setErrCode] = useState(null);
  const [errMsg,  setErrMsg]  = useState(null);

  // No token in URL — show error immediately
  useEffect(() => {
    if (!token) {
      setErrCode("INVALID_TOKEN");
      setErrMsg(null);
    }
  }, [token]);

  if (phase === "loading") return <LoadingState />;
  if (phase === "success") return <SuccessState />;
  if (phase === "error")   return <ErrorState code={errCode} message={errMsg} />;

  // phase === "set_password"
  return (
    <SetPasswordForm
      token={token}
      onSuccess={() => setPhase("success")}
      onError={(code, msg) => {
        setErrCode(code);
        setErrMsg(msg);
        setPhase("error");
      }}
    />
  );
}
